// src/components/WaiverAcceptance.js
// The test drive waiver with its "I agree" checkbox, shown before every
// booking (customer site and kiosk). The text comes from the server so every
// app shows the same version. onChange receives the accepted version, or
// null while it is not accepted.
import React, { useCallback, useEffect, useState } from 'react';
import { waiverAPI } from '../services/api';

const WaiverAcceptance = ({ onChange, large = false }) => {
  const [waiver, setWaiver] = useState(null);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  const load = useCallback(async () => {
    setError('');
    setAccepted(false);
    onChange(null);
    try {
      const { data } = await waiverAPI.getCurrent();
      setWaiver(data);
    } catch (err) {
      console.error('Error loading waiver', err);
      setError('Could not load the test drive waiver.');
    }
  }, [onChange]);

  useEffect(() => { load(); }, [load]);

  const toggle = (checked) => {
    setAccepted(checked);
    onChange(checked && waiver ? waiver.version : null);
  };

  const text = large ? 'text-sm' : 'text-[11px]';

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-2">
      <div className={`font-semibold ${large ? 'text-base' : 'text-sm'}`}>
        {waiver ? waiver.title : 'Test Drive Waiver'}
      </div>

      {error && (
        <div className={`${text} text-red-400`}>
          {error}{' '}
          <button type="button" onClick={load} className="underline">Try again</button>
        </div>
      )}

      {!waiver && !error && (
        <div className={`${text} text-app-text-secondary-light dark:text-app-text-secondary`}>Loading waiver...</div>
      )}

      {waiver && (
        <>
          <div
            className={`max-h-56 overflow-y-auto pr-1 space-y-2 ${text} text-app-text-light dark:text-app-text`}
            tabIndex={0}
            aria-label="Waiver text"
          >
            <p>{waiver.intro}</p>
            <ol className="list-decimal ml-4 space-y-2">
              {waiver.sections.map((section) => (
                <li key={section.heading}>
                  <span className="font-semibold">{section.heading}</span>
                  <br />
                  {section.body}
                </li>
              ))}
            </ol>
          </div>

          <label className="flex items-start gap-2 pt-1 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => toggle(e.target.checked)}
              className={large ? 'mt-1 w-5 h-5' : 'mt-[3px]'}
            />
            <span className={`${text} text-app-text-light dark:text-app-text`}>{waiver.agreement}</span>
          </label>
        </>
      )}
    </div>
  );
};

export default WaiverAcceptance;
